import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export default function useCurrentUser() {
    const authenticatedApi = useAuthenticatedApi();
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<any | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState<Date | null>(null);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [verificationPassword, setVerificationPassword] = useState('');
    const [verificationPasswordError, setVerificationPasswordError] = useState<
        string | null
    >(null);

    useEffect(() => {
        const getUser = async () => {
            const response = await authenticatedApi.get('/accounts/current');
            setUser(response.data);
            setIsLoading(false);
        };

        getUser();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');

    const saveUserDetails = async () => {
        setIsSaving(true);
        try {
            await authenticatedApi.put('/accounts/current', {
                firstName,
                lastName,
            });
            setLastSaved(new Date());
        } catch (error) {
            console.error('Error saving user details', error);
        } finally {
            setIsSaving(false);
        }
    };

    const savePassword = async () => {
        setIsSaving(true);
        try {
            await authenticatedApi.put('/accounts/current/password', {
                oldPassword,
                newPassword,
            });
            toast.success('Password changed successfully');
        } catch (error: any) {
            console.error('Error saving password', error);
            if (error.response.data) {
                toast.error(error.response.data);
            } else {
                toast.error('An error occurred while changing your password');
            }
        } finally {
            setIsSaving(false);
        }
    };

    useDebouncedEffect(saveUserDetails, [firstName, lastName], 2000);

    useEffect(() => {
        if (verificationPassword !== newPassword) {
            setVerificationPasswordError('Passwords do not match');
        } else {
            setVerificationPasswordError(null);
        }
    }, [verificationPassword, newPassword]);

    return {
        isLoading,
        user,
        email,
        firstName,
        lastName,
        setEmail,
        setFirstName,
        setLastName,
        isSaving,
        lastSaved,
        isChangingPassword,
        setIsChangingPassword,
        oldPassword,
        setOldPassword,
        newPassword,
        setNewPassword,
        verificationPassword,
        setVerificationPassword,
        verificationPasswordError,
        savePassword,
    };
}

function useDebouncedEffect(fn: () => void, deps: any[], delay: number) {
    useEffect(() => {
        const handler = setTimeout(() => fn(), delay);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(deps || []), delay]);
}
